// src/components/classroom/LiveKitClassroom.jsx
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import "@livekit/components-styles";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  StartAudio,
  useRoomContext,
  useParticipants,
  useDataChannel,
} from "@livekit/components-react";
import {
  FaExclamationTriangle,
  FaSpinner,
  FaShieldAlt,
  FaArrowLeft,
  FaRedo,
} from "react-icons/fa";
import { toast } from "react-toastify";
import {
  requestMediaCredentials,
  recordClassroomAttendance,
  MediaCredentialError,
} from "./liveKitMediaService";
import ClassroomParticipantGrid from "./ClassroomParticipantGrid";
import ClassroomControls from "./ClassroomControls";
import LegacyP2PClassroom from "./LegacyP2PClassroom";
import { CLASSROOM_DATA_TOPIC, decodeClassroomEvent, encodeClassroomEvent } from "./classroomData";

export default function LiveKitClassroom({
  meetingCode,
  guestToken,
  isHost = false,
  displayName = "Participant",
  onLeave,
  onToggleChat,
  onToggleParticipants,
  showChat = false,
  showParticipants = false,
  forceLegacyP2P = false,
  allowStudentAudio = true,
  allowStudentVideo = false,
  initialMicEnabled = true,
  initialCamEnabled = false,
  onParticipantsChanged,
  onChatMessage,
  onHandRaise,
  onDataSenderReady,
}) {
  const [credentials, setCredentials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useLegacy, setUseLegacy] = useState(forceLegacyP2P);
  const [participantCount, setParticipantCount] = useState(1);
  const [raisedHandIdentities, setRaisedHandIdentities] = useState([]);
  const classroomDataSenderRef = useRef(null);

  // LiveKitRoom observes this object by reference. Recreating it on every
  // render repeatedly invokes connect() while the room is already connected.
  const connectOptions = useMemo(() => ({ autoSubscribe: true }), []);

  const isLeavingRef = useRef(false);

  // Check environment feature flag (defaults to LiveKit enabled)
  const isLiveKitEnabled =
    !useLegacy &&
    process.env.REACT_APP_USE_LIVEKIT !== "false";

  const fetchToken = useCallback(async () => {
    if (!isLiveKitEnabled) return;
    setLoading(true);
    setError(null);

    try {
      const creds = await requestMediaCredentials({
        meetingCode,
        guestToken,
        isHost,
      });
      console.log("[LiveKit] Acquired credentials:", {
        serverUrl: creds.serverUrl,
        roomName: creds.roomName,
        participantIdentity: creds.participantIdentity,
        participantName: creds.participantName,
        role: creds.role,
        host: creds.host,
        allowedPublishSources: creds.allowedPublishSources,
      });
      setCredentials(creds);
    } catch (err) {
      console.error("LiveKit credential acquisition failed:", err);
      // Production may intentionally run on the legacy media path until
      // LiveKit credentials are installed. Do not strand an admitted guest on
      // an error screen when the secured Socket.IO/WebRTC path is available.
      if (err?.code === "LIVEKIT_NOT_CONFIGURED") {
        setUseLegacy(true);
        return;
      }
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [meetingCode, guestToken, isHost, isLiveKitEnabled]);

  useEffect(() => {
    fetchToken();
  }, [fetchToken]);

  // Page unload attendance beacon fallback
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isLeavingRef.current) return;
      isLeavingRef.current = true;
      recordClassroomAttendance(
        meetingCode,
        {
          studentName: displayName,
          leftAt: new Date().toISOString(),
          status: "DISCONNECTED_UNLOAD",
        },
        { keepalive: true }
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [meetingCode, displayName]);

  const handleDisconnected = useCallback(() => {
    if (isLeavingRef.current) return;
    isLeavingRef.current = true;
    toast.info("Classroom session ended.");
    if (typeof onLeave === "function") {
      onLeave();
    }
  }, [onLeave]);

  // Fallback to legacy P2P if explicitly toggled or feature-flagged off
  if (!isLiveKitEnabled) {
    return (
      <LegacyP2PClassroom
        roomId={meetingCode}
        role={isHost ? "teacher" : "student"}
        displayName={displayName}
        guestToken={guestToken}
        onClose={onLeave}
        allowStudentAudio={allowStudentAudio}
        allowStudentVideo={allowStudentVideo}
      />
    );
  }

  // Loading Screen
  if (loading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center min-h-[350px] p-6 text-center text-white bg-slate-950 rounded-2xl">
        <div className="relative mb-4 flex items-center justify-center">
          <span className="animate-ping absolute inline-flex h-12 w-12 rounded-full bg-sky-400 opacity-20" />
          <FaSpinner className="animate-spin text-4xl text-sky-500" />
        </div>
        <p className="text-base font-bold text-slate-100">Connecting to LiveKit Media Room...</p>
        <p className="text-xs text-slate-400 mt-1">
          Validating signed media credentials with the ClassNX backend
        </p>
      </div>
    );
  }

  // Credential Error Screen
  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center min-h-[350px] p-6 text-center text-white bg-slate-950 rounded-2xl">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-500/10 text-rose-500 text-3xl mb-4">
          <FaExclamationTriangle />
        </div>

        <h3 className="text-lg font-bold text-slate-100">Media Connection Failed</h3>
        <p className="text-xs text-rose-400 mt-1 max-w-md">{error.message}</p>

        <div className="mt-4 rounded-xl border border-slate-800 bg-slate-900/60 p-3 text-left text-xs max-w-sm text-slate-400 space-y-1">
          <p className="text-slate-300 font-semibold flex items-center gap-1.5">
            <FaShieldAlt className="text-sky-400" /> Security Diagnostic:
          </p>
          <p className="text-[11px]">
            {error.status === 401 && "Admission token invalid or expired. Please re-knock to enter."}
            {error.status === 403 && "Unauthorized tenant or instructor assignment mismatch."}
            {(error.status === 404 || error.status === 410) && "This lecture is no longer live."}
            {error.status === 500 && "LiveKit server credentials pending on backend."}
            {!error.status && "Network unreachable. Verify connection to backend."}
          </p>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={fetchToken}
            className="inline-flex items-center gap-1.5 rounded-xl bg-sky-600 px-4 py-2 text-xs font-bold text-white hover:bg-sky-500 transition shadow"
          >
            <FaRedo /> Retry Connection
          </button>

          {/* Option to switch to legacy P2P during transition period */}
          <button
            type="button"
            onClick={() => setUseLegacy(true)}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-700 bg-slate-800 px-4 py-2 text-xs font-bold text-slate-300 hover:bg-slate-700 transition"
          >
            Use Legacy WebRTC
          </button>

          <button
            type="button"
            onClick={onLeave}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-800 px-4 py-2 text-xs font-bold text-slate-400 hover:text-white transition"
          >
            <FaArrowLeft /> Exit Class
          </button>
        </div>
      </div>
    );
  }

  if (!credentials) return null;

  return (
    <div className="flex-1 flex flex-col h-full w-full overflow-hidden bg-slate-950 relative">
      <LiveKitRoom
        serverUrl={credentials.serverUrl}
        token={credentials.token}
        connect={true}
        connectOptions={connectOptions}
        // ClassroomControls owns microphone startup after the room reaches
        // Connected. Keeping this false avoids two concurrent capture/publish
        // requests during the lobby-to-LiveKit device handoff.
        audio={false}
        video={
          initialCamEnabled &&
          credentials.allowedPublishSources?.includes("camera")
        }
        onError={(err) => {
          console.warn("LiveKit room media warning:", err);
        }}
        onMediaDeviceFailure={(failure) => {
          console.error("LiveKit media device failure:", failure);
          toast.error("Microphone or camera error. Please ensure your microphone is plugged in and allowed in browser permissions.");
        }}
        onDisconnected={handleDisconnected}
        className="flex-1 flex flex-col h-full w-full overflow-hidden"
      >
        {/* LiveKit's renderer owns one audio element per subscribed remote
            track. Keeping a single renderer prevents duplicate/hidden audio
            elements from fighting over playback state. */}
        <RoomAudioRenderer />
        <ParticipantStateBridge
          onChange={(participants) => {
            setParticipantCount(participants.length);
            onParticipantsChanged?.(participants);
          }}
        />
        <ClassroomDataBridge
          senderRef={classroomDataSenderRef}
          onSenderReady={onDataSenderReady}
          onChatMessage={onChatMessage}
          onHandRaise={(event) => {
            setRaisedHandIdentities((current) => event.raised
              ? [...new Set([...current, event.identity])]
              : current.filter((identity) => identity !== event.identity));
            onHandRaise?.(event);
            if (event.raised) toast.info(`${event.sender || "A participant"} raised a hand.`);
          }}
        />
        <StartAudio
          label="Tap to enable classroom audio"
          className="fixed inset-x-4 bottom-24 z-50 mx-auto max-w-xs rounded-xl bg-sky-600 px-4 py-3 text-sm font-bold text-white shadow-xl hover:bg-sky-500"
        />
        <AudioUnlocker />

        {/* Main Participant Video Stage */}
        <ClassroomParticipantGrid raisedHandIdentities={raisedHandIdentities} />

        {/* Floating / Docked Bottom Controls */}
        <div className="min-h-16 border-t border-slate-800 bg-slate-900/95 px-2 py-2 sm:px-4 flex items-center justify-between gap-2 backdrop-blur-md shrink-0 safe-area-bottom">
          <div className="hidden sm:flex items-center gap-2 shrink-0">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-semibold text-slate-300">
              LiveKit &bull; <strong className="text-white">{credentials.participantName}</strong> ({credentials.role})
            </span>
          </div>

          <ClassroomControls
            credentials={credentials}
            onLeave={handleDisconnected}
            onToggleChat={onToggleChat}
            onToggleParticipants={onToggleParticipants}
            showChat={showChat}
            showParticipants={showParticipants}
            participantCount={participantCount}
            initialMicEnabled={initialMicEnabled}
            sendClassroomEvent={(event) => classroomDataSenderRef.current?.(event)}
            onLocalHandChange={(identity, raised) => setRaisedHandIdentities((current) => raised
              ? [...new Set([...current, identity])]
              : current.filter((item) => item !== identity))}
          />
        </div>
      </LiveKitRoom>
    </div>
  );
}

function ClassroomDataBridge({ senderRef, onSenderReady, onChatMessage, onHandRaise }) {
  const { send } = useDataChannel(CLASSROOM_DATA_TOPIC, (message) => {
    const event = decodeClassroomEvent(message.payload);
    if (!event) return;
    const enriched = {
      ...event,
      identity: event.identity || message.from?.identity,
      sender: event.sender || message.from?.name || message.from?.identity,
    };
    if (enriched.type === "CHAT") onChatMessage?.(enriched);
    if (enriched.type === "HAND") onHandRaise?.(enriched);
  });

  useEffect(() => {
    const sendEvent = async (event) => send(encodeClassroomEvent(event), { reliable: true });
    senderRef.current = sendEvent;
    onSenderReady?.(sendEvent);
    return () => {
      senderRef.current = null;
      onSenderReady?.(null);
    };
  }, [send, senderRef, onSenderReady]);

  return null;
}

function ParticipantStateBridge({ onChange }) {
  const participants = useParticipants();
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onChangeRef.current?.(
      participants.map((participant) => ({
        identity: participant.identity,
        name: participant.name || participant.identity || "Participant",
        isLocal: participant.isLocal,
      }))
    );
  }, [participants]);

  return null;
}

function AudioUnlocker() {
  const room = useRoomContext();
  const [needsUserGesture, setNeedsUserGesture] = useState(false);

  useEffect(() => {
    if (!room) return;
    const checkAudio = () => {
      setNeedsUserGesture(!room.canPlaybackAudio);
    };
    checkAudio();
    room.on("audioPlaybackStatusChanged", checkAudio);

    const unlock = () => {
      if (!room.canPlaybackAudio) {
        room.startAudio().then(() => setNeedsUserGesture(false)).catch(console.warn);
      }
    };
    window.addEventListener("click", unlock, { passive: true });
    window.addEventListener("touchstart", unlock, { passive: true });
    window.addEventListener("keydown", unlock, { passive: true });

    return () => {
      room.off("audioPlaybackStatusChanged", checkAudio);
      window.removeEventListener("click", unlock);
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, [room]);

  if (!needsUserGesture) return null;

  return (
    <div className="fixed inset-x-4 top-20 z-50 mx-auto max-w-sm flex items-center justify-between gap-3 rounded-2xl bg-amber-500/95 backdrop-blur px-4 py-3 text-slate-950 shadow-2xl animate-bounce border border-amber-300">
      <span className="text-xs font-black">
        Audio paused by browser. Click anywhere to listen!
      </span>
      <button
        type="button"
        onClick={() => {
          if (room) {
            room.startAudio().then(() => setNeedsUserGesture(false)).catch(console.warn);
          }
        }}
        className="rounded-xl bg-slate-950 px-3 py-1 text-xs font-bold text-white hover:bg-slate-800"
      >
        Listen Now
      </button>
    </div>
  );
}
